import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { useRegisterPageTitle } from '@/contexts/PageTitleContext';
import { cn } from '@/lib/utils';

export interface Crumb {
    label: string;
    /** Omit on the last crumb to render it as the current page. */
    to?: string;
}

export interface PageHeaderProps {
    /** Page title. Also becomes the document title prefix. */
    title: string;
    /** One short line under the title. */
    description?: React.ReactNode;
    /** Trail shown above the title. The current page is appended automatically. */
    breadcrumbs?: Crumb[];
    /** Primary/secondary actions, right aligned. */
    actions?: React.ReactNode;
    /** Contextual controls (e.g. prev/next paging) shown left of the actions. */
    meta?: React.ReactNode;
    /** Renders a back button left of the title. */
    backTo?: string;
    backLabel?: string;
    /** Overrides the document title when it should differ from the visible title. */
    documentTitle?: string;
    /** Small line above the title (e.g. today's date on the dashboard). */
    eyebrow?: React.ReactNode;
    className?: string;
}

/**
 * The single page header for the CRM.
 *
 * Every CRM page uses this so titles, spacing, breadcrumbs and action placement
 * are identical across screens, and so the document title is set in one place.
 */
export const PageHeader: React.FC<PageHeaderProps> = ({
    title,
    description,
    breadcrumbs,
    actions,
    meta,
    backTo,
    backLabel,
    documentTitle,
    eyebrow,
    className,
}) => {
    const navigate = useNavigate();
    useRegisterPageTitle(documentTitle ?? title);

    const trail = breadcrumbs?.filter(Boolean) ?? [];

    return (
        <div className={cn('flex flex-col gap-3', className)}>
            {trail.length > 0 && (
                <Breadcrumb>
                    <BreadcrumbList>
                        {trail.map((crumb, index) => {
                            const isLast = index === trail.length - 1;
                            return (
                                <React.Fragment key={`${crumb.label}-${index}`}>
                                    <BreadcrumbItem>
                                        {crumb.to && !isLast ? (
                                            <BreadcrumbLink to={crumb.to}>
                                                {crumb.label}
                                            </BreadcrumbLink>
                                        ) : (
                                            <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                                        )}
                                    </BreadcrumbItem>
                                    {!isLast && <BreadcrumbSeparator />}
                                </React.Fragment>
                            );
                        })}
                    </BreadcrumbList>
                </Breadcrumb>
            )}

            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex min-w-0 items-start gap-3">
                    {backTo && (
                        <Button
                            variant="outline"
                            size="icon"
                            className="mt-0.5 h-9 w-9 shrink-0"
                            onClick={() => navigate(backTo)}
                            aria-label={backLabel || 'Go back'}
                            title={backLabel || 'Go back'}
                        >
                            <ArrowLeft className="h-4 w-4" />
                        </Button>
                    )}
                    <div className="min-w-0">
                        {eyebrow && (
                            <p className="text-[13px] font-medium text-muted-foreground">
                                {eyebrow}
                            </p>
                        )}
                        {/* Size is a density token: 24px by default, 20px in the
                            CRM shell. */}
                        <h1 className="truncate text-[length:var(--page-title-size)] font-semibold leading-tight tracking-tight text-foreground">
                            {title}
                        </h1>
                        {description && (
                            <p className="mt-1 text-sm text-muted-foreground">{description}</p>
                        )}
                    </div>
                </div>

                {(meta || actions) && (
                    <div className="flex shrink-0 flex-wrap items-center gap-2">
                        {meta}
                        {actions}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PageHeader;
